import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { invoicesAPI, paymentsAPI, clientsAPI, fiscalAPI } from '../services/api';
import {
  PlusIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CreditCardIcon,
  BanknotesIcon,
  EllipsisHorizontalIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import {
  LoadingSpinner,
  Button,
  Table,
  PermissionGate,
  FileUpload,
  ConfirmModal,
  DataTableCard,
} from '../components/ui';
import PaymentSuccessModal, { PaymentSuccessData } from '../components/payments/PaymentSuccessModal';
import {
  CobrosFiltersSheet,
  CobrosMobileInvoiceCard,
  CobrosMobilePaymentCard,
} from '../components/cobros';
import { Invoice, Client, Payment, TableColumn } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';

/* ─── Constants ─── */

const PAYMENT_METHODS = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA_BANCARIA', label: 'Trans. Bancaria' },
  { value: 'TRANSFERENCIA_MOVIL', label: 'Trans. Móvil' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'DEPOSITO_BANCARIO', label: 'Depósito' },
];

const TRANSFER_METHODS = ['TRANSFERENCIA_BANCARIA', 'TRANSFERENCIA_MOVIL', 'DEPOSITO_BANCARIO'];

const METHOD_LABELS: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA_BANCARIA: 'Transferencia Bancaria',
  TRANSFERENCIA_MOVIL: 'Transferencia Móvil',
  CHEQUE: 'Cheque',
  DEPOSITO_BANCARIO: 'Depósito Bancario',
};

const METHOD_BADGE_COLORS: Record<string, string> = {
  EFECTIVO: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  TRANSFERENCIA_BANCARIA: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  TRANSFERENCIA_MOVIL: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  CHEQUE: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  DEPOSITO_BANCARIO: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
};

const filterInputCls =
  'h-9 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-slate-700 placeholder-slate-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400';

interface RowActionItem {
  label: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
  hidden?: boolean;
}

const RowActionsMenu: React.FC<{ items: RowActionItem[] }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const visible = items.filter(item => !item.hidden);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (visible.length === 0) return null;

  return (
    <div className="relative inline-flex" ref={menuRef}>
      <button
        type="button"
        aria-label="Más acciones"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
      >
        <EllipsisHorizontalIcon className="h-5 w-5" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 min-w-[168px] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
          {visible.map(item => (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors duration-200 ${
                item.tone === 'danger'
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const INVOICE_STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Borrador' },
  { value: 'ISSUED', label: 'Emitida' },
  { value: 'PARTIAL', label: 'Pago parcial' },
  { value: 'PAID', label: 'Pagada' },
  { value: 'OVERDUE', label: 'Vencida' },
  { value: 'CANCELLED', label: 'Anulada' },
];

/** Filtros de estado para el tab Pendiente de cobro */
const PENDING_STATUS_FILTERS = [
  { value: '', label: 'Todos' },
  { value: 'ISSUED', label: 'Pendiente' },
  { value: 'PARTIAL', label: 'Parcial' },
  { value: 'OVERDUE', label: 'Vencida' },
];

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  CONFIRMED: 'Confirmado',
  CANCELLED: 'Anulado',
  REFUNDED: 'Reembolsado',
  PARTIAL_REFUND: 'Reemb. parcial',
};

const inputCls =
  'w-full px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white text-slate-900 placeholder-slate-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400';

const formatMoney = (amount: number) =>
  `$${Number(amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const toIso = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { start: toIso(start), end: toIso(end) };
};

/* ─── Helpers ─── */

const invoiceStatusTone = (status: string): 'success' | 'danger' | 'neutral' | 'info' | 'warning' => {
  if (status === 'PAID') return 'success';
  if (status === 'OVERDUE') return 'danger';
  if (status === 'CANCELLED') return 'neutral';
  if (status === 'ISSUED' || status === 'PARTIAL' || status === 'PENDING') return 'warning';
  return 'info';
};

const invoiceStatusBadgeClass = (status: string) => {
  const tone = invoiceStatusTone(status);
  const map = {
    success: 'bg-green-50 text-green-700 ring-green-200',
    danger: 'bg-red-50 text-red-700 ring-red-200',
    warning: 'bg-amber-50 text-amber-700 ring-amber-200',
    neutral: 'bg-slate-100 text-slate-600 ring-slate-200',
    info: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  } as const;
  return map[tone];
};

const invoiceStatusLabel = (status: string) => {
  if (status === 'ISSUED') return 'Pendiente';
  return INVOICE_STATUS_OPTIONS.find(s => s.value === status)?.label || status;
};

const paymentStatusTone = (
  status: string
): 'success' | 'danger' | 'neutral' | 'info' | 'warning' => {
  if (status === 'CONFIRMED') return 'success';
  if (status === 'CANCELLED') return 'danger';
  if (status === 'REFUNDED' || status === 'PARTIAL_REFUND') return 'neutral';
  return 'warning';
};

const paymentStatusBadgeClass = (status: string) => {
  const tone = paymentStatusTone(status);
  const map = {
    success: 'bg-green-50 text-green-700 ring-green-200',
    danger: 'bg-red-50 text-red-700 ring-red-200',
    warning: 'bg-amber-50 text-amber-700 ring-amber-200',
    neutral: 'bg-slate-100 text-slate-600 ring-slate-200',
    info: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  } as const;
  return map[tone];
};

const ModernBadge: React.FC<{ className: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 transition-colors duration-200 ${className}`}
  >
    {children}
  </span>
);

const getInvoiceBalance = (invoice: Invoice) =>
  (invoice as any).balanceDueMinor != null
    ? (invoice as any).balanceDueMinor / 100
    : Number((invoice as any).amountDue || 0);

const clientFullName = (c: Client) => `${c.name} ${(c as any).lastname || ''}`.trim();

const clientInitials = (c: Client) => {
  const first = (c.name || '').trim().charAt(0);
  const last = ((c as any).lastname || '').trim().charAt(0);
  return `${first}${last}`.toUpperCase() || 'C';
};

const deriveClientStatus = (pendingBalance: number, hasOverdue: boolean) => {
  if (hasOverdue) return { label: 'Vencido', className: 'bg-red-50 text-red-700 ring-red-200' };
  if (pendingBalance > 0) {
    return { label: 'Con deuda', className: 'bg-amber-50 text-amber-700 ring-amber-200' };
  }
  return { label: 'Al día', className: 'bg-green-50 text-green-700 ring-green-200' };
};

const PanelSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="space-y-3 rounded-xl bg-white p-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p>
    {children}
  </div>
);

/* ─── KPI strip ─── */

interface CobrosKpiStripProps {
  totalPorCobrar: number;
  vencido: number;
  pendiente: number;
  cobradoPeriodo: number;
  periodLabel: string;
  loading?: boolean;
}

const CobrosKpiStrip: React.FC<CobrosKpiStripProps> = ({
  totalPorCobrar,
  vencido,
  pendiente,
  cobradoPeriodo,
  periodLabel,
  loading,
}) => {
  const items = [
    {
      key: 'total',
      label: 'Total por cobrar',
      value: totalPorCobrar,
      icon: BanknotesIcon,
      iconWrap: 'bg-indigo-50 text-indigo-600',
      valueClass: 'text-slate-900',
    },
    {
      key: 'vencido',
      label: 'Vencido',
      value: vencido,
      icon: ExclamationTriangleIcon,
      iconWrap: 'bg-red-50 text-red-500',
      valueClass: 'text-red-600',
    },
    {
      key: 'pendiente',
      label: 'Pendiente',
      value: pendiente,
      icon: ClockIcon,
      iconWrap: 'bg-amber-50 text-amber-500',
      valueClass: 'text-amber-600',
    },
    {
      key: 'cobrado',
      label: `Cobrado · ${periodLabel}`,
      value: cobradoPeriodo,
      icon: CheckCircleIcon,
      iconWrap: 'bg-green-50 text-green-500',
      valueClass: 'text-green-600',
    },
  ] as const;

  return (
    <div className="grid grid-cols-3 gap-2 md:grid-cols-2 md:gap-6 xl:grid-cols-4">
      {items.map(item => {
        const Icon = item.icon;
        const hideOnMobile = item.key === 'cobrado';
        return (
          <div
            key={item.key}
            className={`rounded-2xl border border-[#E8ECF3] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 md:rounded-xl md:border-gray-200 md:p-4 md:shadow-sm md:hover:-translate-y-0.5 md:hover:shadow-md ${
              hideOnMobile ? 'hidden md:block' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2 md:gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-sm md:font-normal md:normal-case">
                  {item.label}
                </p>
                <p
                  className={`mt-1 truncate text-base font-bold tracking-tight md:mt-2 md:text-3xl ${item.valueClass}`}
                >
                  {loading ? '…' : formatMoney(item.value)}
                </p>
              </div>
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl md:h-10 md:w-10 ${item.iconWrap}`}
              >
                <Icon className="h-4 w-4 md:h-5 md:w-5" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── ClientSearchBar ─── */

interface ClientSearchBarProps {
  clients: Client[];
  selectedClient: Client | null;
  pendingInvoices: Invoice[];
  onSelect: (client: Client) => void;
  onClear: () => void;
  navigate: ReturnType<typeof useNavigate>;
}

const ClientSearchBar: React.FC<ClientSearchBarProps> = ({
  clients,
  selectedClient,
  pendingInvoices,
  onSelect,
  onClear,
  navigate,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(
    () =>
      clients
        .filter(c =>
          `${clientFullName(c)} ${c.cedula}`.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10),
    [clients, query]
  );

  if (selectedClient) {
    const totalDebt = pendingInvoices.reduce((sum, inv) => sum + getInvoiceBalance(inv), 0);
    const hasOverdue = pendingInvoices.some(inv => inv.status === 'OVERDUE');
    const status = deriveClientStatus(totalDebt, hasOverdue);

    return (
      <>
        {/* Mobile compact banner */}
        <div className="rounded-2xl border border-[#E5EAF1] bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:hidden">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {clientFullName(selectedClient)}
                </p>
                <ModernBadge className={status.className}>{status.label}</ModernBadge>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {pendingInvoices.length} factura(s) pendiente(s)
                {totalDebt > 0 ? ` · ${formatMoney(totalDebt)}` : ''}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2 text-xs font-semibold">
              <button type="button" onClick={onClear} className="text-primary-600">
                Cambiar
              </button>
              <button type="button" onClick={onClear} className="text-slate-500">
                Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Desktop banner */}
        <div className="hidden rounded-xl border border-indigo-100 bg-[#F8FAFF] p-4 shadow-sm transition-all duration-200 md:block">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-base font-bold text-indigo-700 ring-4 ring-white">
                {clientInitials(selectedClient)}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-lg font-semibold text-slate-900">
                    {clientFullName(selectedClient)}
                  </p>
                  <ModernBadge className={status.className}>{status.label}</ModernBadge>
                </div>
                <p className="mt-0.5 text-sm text-slate-500">Documento · {selectedClient.cedula}</p>
                <p className="mt-2 text-sm text-slate-600">
                  Saldo pendiente{' '}
                  <span className={`font-semibold ${totalDebt > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatMoney(totalDebt)}
                  </span>
                  {pendingInvoices.length > 0 && (
                    <span className="text-slate-400"> · {pendingInvoices.length} factura(s)</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(`/clients/${selectedClient.id}`)}
                className="inline-flex h-9 items-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              >
                Ver cliente
              </button>
              <button
                type="button"
                onClick={onClear}
                aria-label="Quitar cliente seleccionado"
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 text-sm font-medium text-indigo-600 transition-colors duration-200 hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              >
                <XMarkIcon className="h-4 w-4" />
                Cambiar
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar por cliente, factura o documento…"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="h-11 w-full rounded-2xl border border-[#E5EAF1] bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] placeholder-slate-400 transition-colors duration-200 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 md:h-auto md:rounded-xl md:border-gray-200 md:shadow-sm md:focus:border-indigo-400 md:focus:ring-indigo-200"
        />
      </div>

      {open && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-30 mt-1.5 max-h-[280px] w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl"
        >
          {filtered.map(client => (
            <button
              key={client.id}
              type="button"
              onClick={() => {
                onSelect(client);
                setQuery('');
                setOpen(false);
              }}
              className="flex w-full items-center gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors duration-200 last:border-0 hover:bg-slate-50"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                {clientInitials(client)}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">{clientFullName(client)}</p>
                <p className="text-xs text-slate-500">{client.cedula}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && query.length > 1 && filtered.length === 0 && (
        <div className="absolute z-30 mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-lg">
          <p className="text-sm text-slate-500">No se encontraron clientes</p>
        </div>
      )}
    </div>
  );
};

/* ─── InvoicePanel ─── */

interface InvoicePanelProps {
  invoice?: Invoice | null;
  /** Prefills create form without switching to edit mode */
  duplicateFrom?: Invoice | null;
  clients: Client[];
  lockedClientId?: string;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
  isSaving: boolean;
}

const InvoicePanel: React.FC<InvoicePanelProps> = ({
  invoice,
  duplicateFrom = null,
  clients,
  lockedClientId,
  onClose,
  onSave,
  isSaving,
}) => {
  const isEdit = Boolean(invoice);
  const seed = invoice || duplicateFrom;

  const [formData, setFormData] = useState({
    clientId: lockedClientId || seed?.clientId?.toString() || '',
    invoiceNumber: isEdit ? ((invoice as any)?.invoiceNumber || '') : '',
    amountDue: seed?.amountDue || 0,
    dueDate: seed?.dueDate ? new Date(seed.dueDate).toISOString().split('T')[0] : '',
    status: isEdit ? (invoice?.status || 'DRAFT') : 'DRAFT',
    notes: isEdit ? ((invoice as any)?.notes || '') : ((duplicateFrom as any)?.notes || ''),
  });

  // Keep lockedClientId in sync when panel is re-opened
  useEffect(() => {
    if (lockedClientId) {
      setFormData(prev => ({ ...prev, clientId: lockedClientId }));
    }
  }, [lockedClientId]);

  const [clientSearch, setClientSearch] = useState(() => {
    const init = lockedClientId
      ? clients.find(c => String(c.id) === lockedClientId)
      : seed?.client;
    return init ? clientFullName(init as Client) : '';
  });

  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        clientInputRef.current &&
        !clientInputRef.current.contains(e.target as Node)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredClients = useMemo(
    () =>
      clients
        .filter(c =>
          `${clientFullName(c)} ${c.cedula}`.toLowerCase().includes(clientSearch.toLowerCase())
        )
        .slice(0, 8),
    [clients, clientSearch]
  );

  const selectedClient = clients.find(c => String(c.id) === formData.clientId);
  const isClientLocked = Boolean(lockedClientId) || isEdit;

  const handleSelectClient = (client: Client) => {
    setClientSearch(clientFullName(client));
    setFormData(prev => ({ ...prev, clientId: String(client.id) }));
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setClientSearch('');
    setFormData(prev => ({ ...prev, clientId: '' }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amountDue' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) { toast.error('Seleccione un cliente'); return; }
    if (!formData.dueDate) { toast.error('Ingrese la fecha de vencimiento'); return; }
    const payload: Record<string, unknown> = {
      clientId: Number(formData.clientId),
      invoiceNumber: formData.invoiceNumber || undefined,
      dueDate: formData.dueDate,
      subtotal: Number(formData.amountDue || 0),
      discountTotal: 0,
      taxTotal: 0,
      moraTotal: 0,
      status: formData.status || 'DRAFT',
      notes: formData.notes || undefined,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 top-[6%] z-50 flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl lg:inset-auto lg:top-6 lg:z-auto lg:sticky lg:max-h-[calc(100vh-140px)] lg:w-[420px] lg:shrink-0 lg:rounded-xl lg:border lg:border-gray-200 lg:shadow-sm">
      {/* Header */}
      <div className="flex shrink-0 flex-col items-center border-b border-gray-100 bg-white px-5 pb-4 pt-2 lg:items-stretch lg:pt-4">
        <div className="mb-2 h-1 w-10 rounded-full bg-slate-300 lg:hidden" aria-hidden />
        <div className="flex w-full items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {isEdit ? 'Editar factura' : duplicateFrom ? 'Duplicar factura' : 'Nueva factura'}
            </h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {isEdit
                ? 'Modifica los datos del registro'
                : duplicateFrom
                ? 'Se copiaron los datos; revisa y guarda'
                : 'Complete los campos para crear'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar panel"
            className="rounded-lg p-1.5 text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-[#F6F8FC]">
        <form onSubmit={handleSubmit} id="invoice-form">
          <div className="space-y-4 p-4">
            <PanelSection title="Cliente *">
              {selectedClient ? (
                <div className="flex items-center gap-3 rounded-xl border border-indigo-100 bg-[#F8FAFF] px-3 py-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                    {clientInitials(selectedClient)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-indigo-900">
                      {clientFullName(selectedClient)}
                    </p>
                    <p className="text-xs text-indigo-600">{selectedClient.cedula}</p>
                  </div>
                  {!isClientLocked && (
                    <button
                      type="button"
                      onClick={handleClearClient}
                      aria-label="Quitar cliente"
                      className="text-indigo-400 transition-colors duration-200 hover:text-indigo-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                  {isClientLocked && (
                    <span className="rounded-md bg-indigo-100 px-2 py-0.5 text-xs text-indigo-500">
                      fijo
                    </span>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      ref={clientInputRef}
                      type="text"
                      placeholder="Buscar por nombre o cédula…"
                      value={clientSearch}
                      onChange={e => {
                        setClientSearch(e.target.value);
                        setShowClientDropdown(true);
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      className={inputCls + ' pl-9'}
                    />
                  </div>
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-30 mt-1 max-h-[200px] w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
                    >
                      {filteredClients.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="flex w-full items-center gap-3 border-b border-slate-50 px-3 py-2.5 text-left transition-colors duration-200 last:border-0 hover:bg-slate-50"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-700">
                            {clientInitials(client)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{clientFullName(client)}</p>
                            <p className="text-xs text-slate-500">{client.cedula}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showClientDropdown && clientSearch.length > 1 && filteredClients.length === 0 && (
                    <div className="absolute z-30 mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-lg">
                      <p className="text-sm text-slate-500">No se encontraron clientes</p>
                    </div>
                  )}
                </div>
              )}
            </PanelSection>

            <PanelSection title="Datos de la factura">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Nro. factura</label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleChange}
                    placeholder="Auto-generado"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Monto *</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      name="amountDue"
                      value={formData.amountDue || ''}
                      onChange={handleChange}
                      placeholder="0.00"
                      required
                      className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition-colors duration-200 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                </div>
              </div>
            </PanelSection>

            <PanelSection title="Vencimiento y estado">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">
                    Fecha vencimiento *
                  </label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    required
                    aria-label="Fecha de vencimiento"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Estado</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    aria-label="Estado de la factura"
                    className={inputCls}
                  >
                    {INVOICE_STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </PanelSection>

            <PanelSection title="Notas">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Observaciones adicionales (opcional)"
                className={`${inputCls} resize-none`}
              />
            </PanelSection>

            {formData.amountDue > 0 && (
              <PanelSection title="Resumen">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Monto</span>
                  <span className="text-xl font-bold text-slate-900">
                    ${Number(formData.amountDue).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </PanelSection>
            )}
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="shrink-0 space-y-2 border-t border-gray-100 bg-white px-4 py-4">
        <button
          type="submit"
          form="invoice-form"
          disabled={isSaving || !formData.clientId}
          className="inline-flex w-full items-center justify-center rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? 'Guardando…' : isEdit ? 'Actualizar' : 'Crear factura'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors duration-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

/* ─── PaymentPanel ─── */

interface PaymentPanelProps {
  payment?: Payment | null;
  clients: Client[];
  lockedClientId?: string;
  /** Prefill client, amount and allocation for one-click cobro from a row */
  quickPayInvoice?: Invoice | null;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
  isSaving: boolean;
}

const PaymentPanel: React.FC<PaymentPanelProps> = ({
  payment,
  clients,
  lockedClientId,
  quickPayInvoice = null,
  onClose,
  onSave,
  isSaving,
}) => {
  const { hasPermission } = usePermissions();
  const canVerify = hasPermission('payments.verify');
  const isEdit = Boolean(payment);

  const quickPayClientId = quickPayInvoice
    ? String(quickPayInvoice.clientId || (quickPayInvoice as any).client?.id || '')
    : '';
  const effectiveLockedClientId = lockedClientId || quickPayClientId || undefined;
  const quickPayBalance = quickPayInvoice ? getInvoiceBalance(quickPayInvoice) : 0;

  const [formData, setFormData] = useState({
    amount: payment?.amount || (quickPayInvoice ? quickPayBalance : 0),
    paymentDate: payment?.paymentDate
      ? new Date(payment.paymentDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    paymentMethod: (payment?.paymentMethod || 'EFECTIVO') as string,
    reference: payment?.reference || '',
    notes: payment?.notes || '',
    clientId: effectiveLockedClientId || payment?.clientId?.toString() || '',
    bankReference: (payment as any)?.bankReference || '',
    bankName: (payment as any)?.bankName || '',
    accountNumber: (payment as any)?.accountNumber || '',
    transferDate: (payment as any)?.transferDate
      ? new Date((payment as any).transferDate).toISOString().split('T')[0]
      : '',
    receiptFile: (payment as any)?.receiptFile || '',
    isVerified: payment?.isVerified || false,
  });

  useEffect(() => {
    if (effectiveLockedClientId) {
      setFormData(prev => ({ ...prev, clientId: effectiveLockedClientId }));
    }
  }, [effectiveLockedClientId]);

  const [allocations, setAllocations] = useState<Record<string, string>>(() => {
    if (quickPayInvoice) {
      return { [String(quickPayInvoice.id)]: quickPayBalance.toFixed(2) };
    }
    return {};
  });

  const [clientSearch, setClientSearch] = useState(() => {
    const init = effectiveLockedClientId
      ? clients.find(c => String(c.id) === effectiveLockedClientId)
      : payment?.client;
    return init ? clientFullName(init as Client) : '';
  });

  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        clientInputRef.current && !clientInputRef.current.contains(e.target as Node)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: clientInvoices, isLoading: invoicesLoading } = useQuery<{
    data: { data: Invoice[] };
  }>(
    ['pending-invoices', formData.clientId],
    () => paymentsAPI.getPendingInvoices(parseInt(formData.clientId)),
    { enabled: Boolean(formData.clientId) && !isEdit, staleTime: 30_000 }
  );

  const filteredClients = useMemo(
    () =>
      clients
        .filter(c =>
          `${clientFullName(c)} ${c.cedula}`.toLowerCase().includes(clientSearch.toLowerCase())
        )
        .slice(0, 8),
    [clients, clientSearch]
  );

  const selectedClient = clients.find(c => String(c.id) === formData.clientId);
  const invoiceList: Invoice[] = clientInvoices?.data?.data || [];
  const isTransferMethod = TRANSFER_METHODS.includes(formData.paymentMethod);
  const isClientLocked = Boolean(effectiveLockedClientId) || isEdit;

  // Keep quick-pay allocation applied once pending invoices load
  useEffect(() => {
    if (!quickPayInvoice || isEdit) return;
    const key = String(quickPayInvoice.id);
    const bal = getInvoiceBalance(quickPayInvoice);
    setAllocations(prev => {
      if (prev[key] !== undefined) return prev;
      return { ...prev, [key]: bal.toFixed(2) };
    });
    setFormData(prev => (prev.amount > 0 ? prev : { ...prev, amount: bal }));
  }, [quickPayInvoice, isEdit, invoiceList.length]);

  const allocatedTotal = Object.values(allocations).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0
  );
  const creditBalance = Math.max(formData.amount - allocatedTotal, 0);
  const overdraft = allocatedTotal > formData.amount + 0.001;

  const handleSelectClient = (client: Client) => {
    setClientSearch(clientFullName(client));
    setFormData(prev => ({ ...prev, clientId: String(client.id), amount: 0 }));
    setAllocations({});
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setClientSearch('');
    setFormData(prev => ({ ...prev, clientId: '', amount: 0 }));
    setAllocations({});
  };

  const toggleAllocation = (invoice: Invoice) => {
    const key = String(invoice.id);
    setAllocations(prev => {
      if (prev[key] !== undefined) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: String(getInvoiceBalance(invoice).toFixed(2)) };
    });
  };

  const updateAllocationAmount = (invoiceId: string, value: string) => {
    setAllocations(prev => ({ ...prev, [invoiceId]: value }));
  };

  const fillAllPending = () => {
    let remaining = formData.amount;
    const next: Record<string, string> = {};
    for (const inv of invoiceList) {
      if (remaining <= 0) break;
      const bal = getInvoiceBalance(inv);
      const apply = Math.min(bal, remaining);
      next[String(inv.id)] = apply.toFixed(2);
      remaining -= apply;
    }
    setAllocations(next);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'amount'
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const buildPayload = (): Record<string, unknown> => {
    const base: Record<string, unknown> = {
      clientId: parseInt(formData.clientId, 10),
      amount: formData.amount,
      paymentDate: formData.paymentDate,
      paymentMethod: formData.paymentMethod,
      reference: formData.reference || undefined,
      notes: formData.notes || undefined,
      isVerified: formData.isVerified,
    };

    if (isTransferMethod) {
      base.bankReference = formData.bankReference || undefined;
      base.bankName = formData.bankName || undefined;
      base.accountNumber = formData.accountNumber || undefined;
      base.transferDate = formData.transferDate || undefined;
      base.receiptFile = formData.receiptFile || undefined;
    }

    if (!isEdit) {
      const allocationArray = Object.entries(allocations)
        .filter(([, v]) => parseFloat(v) > 0)
        .map(([invoiceId, v]) => ({
          invoiceId: parseInt(invoiceId, 10),
          amountMinor: Math.round(parseFloat(v) * 100),
        }));
      if (allocationArray.length > 0) base.allocations = allocationArray;
    }

    return base;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) { toast.error('Seleccione un cliente'); return; }
    if (!formData.amount || formData.amount <= 0) { toast.error('Ingrese un monto válido'); return; }
    if (overdraft) { toast.error('El monto aplicado a facturas supera el total del pago'); return; }
    onSave(buildPayload());
  };

    return (
    <div className="fixed inset-x-0 bottom-0 top-[6%] z-50 flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl lg:inset-auto lg:top-6 lg:z-auto lg:sticky lg:max-h-[calc(100vh-140px)] lg:w-[420px] lg:shrink-0 lg:rounded-xl lg:border lg:border-gray-200 lg:shadow-sm">
      {/* Header */}
      <div className="flex shrink-0 flex-col items-center border-b border-gray-100 bg-white px-5 pb-4 pt-2 lg:items-stretch lg:pt-4">
        <div className="mb-2 h-1 w-10 rounded-full bg-slate-300 lg:hidden" aria-hidden />
        <div className="flex w-full items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {isEdit ? 'Editar pago' : quickPayInvoice ? 'Cobrar factura' : 'Registrar pago'}
            </h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {isEdit
                ? 'Modifica los datos del registro'
                : quickPayInvoice
                ? `Factura ${(quickPayInvoice.invoiceNumber as string) || `#${quickPayInvoice.id}`} · ${formatMoney(quickPayBalance)}`
                : 'Complete los campos para registrar'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar panel"
            className="rounded-lg p-1.5 text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-[#F6F8FC]">
        <form onSubmit={handleSubmit} id="payment-form">
          <div className="space-y-4 p-4">
            <PanelSection title="Cliente *">
              {selectedClient ? (
                <div className="flex items-center gap-3 rounded-xl border border-indigo-100 bg-[#F8FAFF] px-3 py-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                    {clientInitials(selectedClient)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-indigo-900">
                      {clientFullName(selectedClient)}
                    </p>
                    <p className="text-xs text-indigo-600">{selectedClient.cedula}</p>
                  </div>
                  {!isClientLocked ? (
                    <button
                      type="button"
                      onClick={handleClearClient}
                      aria-label="Quitar cliente"
                      className="text-indigo-400 transition-colors duration-200 hover:text-indigo-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="rounded-md bg-indigo-100 px-2 py-0.5 text-xs text-indigo-500">
                      fijo
                    </span>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      ref={clientInputRef}
                      type="text"
                      placeholder="Buscar por nombre o cédula…"
                      value={clientSearch}
                      onChange={e => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                      onFocus={() => setShowClientDropdown(true)}
                      className={inputCls + ' pl-9'}
                    />
                  </div>
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-30 mt-1 max-h-[200px] w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
                    >
                      {filteredClients.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="flex w-full items-center gap-3 border-b border-slate-50 px-3 py-2.5 text-left transition-colors duration-200 last:border-0 hover:bg-slate-50"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-700">
                            {clientInitials(client)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{clientFullName(client)}</p>
                            <p className="text-xs text-slate-500">{client.cedula}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showClientDropdown && clientSearch.length > 1 && filteredClients.length === 0 && (
                    <div className="absolute z-30 mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-lg">
                      <p className="text-sm text-slate-500">No se encontraron clientes</p>
                    </div>
                  )}
                </div>
              )}
            </PanelSection>

            <PanelSection title="Monto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Monto *</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      name="amount"
                      value={formData.amount || ''}
                      onChange={handleChange}
                      placeholder="0.00"
                      required
                      className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition-colors duration-200 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Fecha *</label>
                  <input
                    type="date"
                    name="paymentDate"
                    value={formData.paymentDate}
                    onChange={handleChange}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    aria-label="Fecha del pago"
                    className={inputCls}
                  />
                </div>
              </div>
            </PanelSection>

            <PanelSection title="Método de pago">
              <div className="flex flex-wrap gap-2">
                {PAYMENT_METHODS.map(m => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, paymentMethod: m.value }))}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                      formData.paymentMethod === m.value
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200'
                        : 'border-gray-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Referencia</label>
                <input
                  type="text"
                  name="reference"
                  value={formData.reference}
                  onChange={handleChange}
                  placeholder="Número de referencia del pago"
                  className={inputCls}
                />
              </div>
            </PanelSection>

            {isTransferMethod && (
              <PanelSection title="Datos bancarios">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-600">Ref. bancaria</label>
                    <input type="text" name="bankReference" value={formData.bankReference} onChange={handleChange} placeholder="Nro. referencia" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-600">Banco</label>
                    <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} placeholder="Nombre del banco" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-600">N° cuenta</label>
                    <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleChange} placeholder="Número de cuenta" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-600">Fecha transf.</label>
                    <input type="date" name="transferDate" value={formData.transferDate} onChange={handleChange} aria-label="Fecha de la transferencia" className={inputCls} />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Comprobante</label>
                  <FileUpload
                    onFileSelect={file => {
                      if (!file) setFormData(prev => ({ ...prev, receiptFile: '' }));
                    }}
                    onFileUpload={async file => {
                      const r = await paymentsAPI.uploadReceipt(file);
                      const filePath: string = r.data.data.filePath;
                      setFormData(prev => ({ ...prev, receiptFile: filePath }));
                      return filePath;
                    }}
                    accept="image/*,.pdf"
                    maxSize={5}
                  />
                  {formData.receiptFile && (
                    <p className="mt-1 truncate text-xs font-medium text-green-600">
                      ✓ {formData.receiptFile}
                    </p>
                  )}
                </div>

                {canVerify && (
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      name="isVerified"
                      checked={formData.isVerified}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                    />
                    <span className="text-sm text-slate-700">Marcar como verificado</span>
                  </label>
                )}
              </PanelSection>
            )}

            {!isEdit && formData.clientId && (
              <PanelSection title="Aplicación a facturas">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Selecciona facturas a cubrir</span>
                  {invoiceList.length > 0 && formData.amount > 0 && (
                    <button
                      type="button"
                      onClick={fillAllPending}
                      className="text-xs font-medium text-indigo-600 transition-colors duration-200 hover:text-indigo-800"
                    >
                      Aplicar a todas
                    </button>
                  )}
                </div>

                {invoicesLoading ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-slate-500">
                    <LoadingSpinner size="sm" />
                    <span>Cargando facturas…</span>
                  </div>
                ) : invoiceList.length === 0 ? (
                  <p className="py-1 text-xs text-slate-500">
                    Sin facturas pendientes — el pago quedará como crédito.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {invoiceList.map(invoice => {
                      const balance = getInvoiceBalance(invoice);
                      const key = String(invoice.id);
                      const isSelected = allocations[key] !== undefined;
                      const allocationAmt = allocations[key] ?? '';
                      return (
                        <div
                          key={invoice.id}
                          className={`cursor-pointer rounded-xl border transition-all duration-200 ${
                            isSelected
                              ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200'
                              : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-slate-50'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleAllocation(invoice)}
                            className="flex w-full items-center justify-between px-3 py-2.5 text-left"
                          >
                            <div className="flex items-center gap-2.5">
                              <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors duration-200 ${
                                isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-400 bg-white'
                              }`}>
                                {isSelected && <CheckCircleIcon className="h-3.5 w-3.5 text-white" />}
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${isSelected ? 'text-indigo-800' : 'text-slate-800'}`}>
                                  {(invoice as any).invoiceNumber || `Factura #${invoice.id}`}
                                </p>
                                <p className={`mt-0.5 text-xs ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`}>
                                  Saldo: ${balance.toLocaleString('es-DO', { minimumFractionDigits: 2 })} · Vence: {new Date((invoice as any).dueDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </button>

                          {isSelected && (
                            <div className="px-3 pb-2.5">
                              <div className="flex items-center gap-2">
                                <span className="shrink-0 text-xs text-slate-500">Monto a aplicar:</span>
                                <div className="relative flex-1">
                                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={balance}
                                    value={allocationAmt}
                                    onChange={e => updateAllocationAmount(key, e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    className="w-full rounded-lg border border-indigo-300 bg-white py-1.5 pl-6 pr-2 text-sm transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                                    aria-label={`Monto a aplicar a factura ${invoice.id}`}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateAllocationAmount(key, String(balance.toFixed(2)))}
                                  className="shrink-0 text-xs text-slate-400 transition-colors duration-200 hover:text-indigo-600"
                                  title="Completar saldo"
                                >
                                  Max
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {Object.keys(allocations).length > 0 && (
                  <div className={`mt-2 space-y-1 rounded-xl border p-3 text-xs ${
                    overdraft ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-slate-50'
                  }`}>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Total pago</span>
                      <span className="font-semibold text-slate-800">
                        ${formData.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Aplicado a facturas</span>
                      <span className={`font-semibold ${overdraft ? 'text-red-700' : 'text-slate-800'}`}>
                        ${allocatedTotal.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {overdraft ? (
                      <div className="flex items-center gap-1 pt-1 font-medium text-red-600">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                        Monto aplicado supera el total del pago
                      </div>
                    ) : creditBalance > 0.005 ? (
                      <div className="flex justify-between border-t border-slate-200 pt-1">
                        <span className="font-medium text-green-600">Crédito a favor</span>
                        <span className="font-bold text-green-700">
                          ${creditBalance.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between border-t border-slate-200 pt-1">
                        <span className="font-medium text-green-600">Saldo cubierto</span>
                        <span className="text-green-600">✓</span>
                      </div>
                    )}
                  </div>
                )}
              </PanelSection>
            )}

            <PanelSection title="Notas">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Observaciones adicionales (opcional)"
                className={`${inputCls} resize-none`}
              />
            </PanelSection>

            {formData.amount > 0 && (
              <PanelSection title="Resumen">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Total</span>
                  <span className="text-xl font-bold text-slate-900">
                    ${formData.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {!isEdit && Object.keys(allocations).length > 0 && creditBalance > 0.005 && (
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-green-600">Crédito</span>
                    <span className="font-semibold text-green-700">
                      +${creditBalance.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </PanelSection>
            )}
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="shrink-0 space-y-2 border-t border-gray-100 bg-white px-4 py-4">
        <button
          type="submit"
          form="payment-form"
          disabled={isSaving || !formData.clientId || !formData.amount || overdraft}
          className="inline-flex w-full items-center justify-center rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? 'Guardando…' : isEdit ? 'Actualizar' : 'Registrar'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors duration-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

/* ─── Tab type ─── */
type ActiveTab = 'pendiente' | 'facturas' | 'pagos';
type PanelMode = 'invoice' | 'payment' | null;

function formatMonthlyInvoiceSchedule(config?: {
  monthlyInvoiceAutoEnabled?: boolean;
  monthlyInvoiceGenerationDay?: number;
  monthlyInvoiceGenerationHour?: number;
} | null): string {
  if (!config?.monthlyInvoiceAutoEnabled) {
    return 'La generación automática de cuotas está desactivada. Configure el día y hora en Configuración → Facturación automática, o use «Generar mensual».';
  }
  const day = config.monthlyInvoiceGenerationDay ?? 1;
  const hour = config.monthlyInvoiceGenerationHour ?? 9;
  const hourLabel = `${String(hour).padStart(2, '0')}:00`;
  return `Las cuotas mensuales se generan automáticamente el día ${day} de cada mes (${hourLabel}); use «Generar mensual» solo si necesita adelantarlas.`;
}

/* ─── Main Cobros page ─── */
const Cobros: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  // Global state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('pendiente');
  const [panelMode, setPanelMode] = useState<PanelMode>(null);
  const [quickPayInvoice, setQuickPayInvoice] = useState<Invoice | null>(null);
  const [duplicateInvoice, setDuplicateInvoice] = useState<Invoice | null>(null);

  // Invoice sub-state
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceFilterStatus, setInvoiceFilterStatus] = useState('');
  const [invoiceFilterStart, setInvoiceFilterStart] = useState('');
  const [invoiceFilterEnd, setInvoiceFilterEnd] = useState('');
  const [invoicePage, setInvoicePage] = useState(1);

  // Pending collection sub-state
  const [pendingFilterStatus, setPendingFilterStatus] = useState('');
  const [pendingFilterStart, setPendingFilterStart] = useState('');
  const [pendingFilterEnd, setPendingFilterEnd] = useState('');
  const [pendingPage, setPendingPage] = useState(1);

  // Payment sub-state
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [paymentFilterMethod, setPaymentFilterMethod] = useState('');
  const [paymentFilterStatus, setPaymentFilterStatus] = useState('');
  const [paymentFilterStart, setPaymentFilterStart] = useState('');
  const [paymentFilterEnd, setPaymentFilterEnd] = useState('');
  const [paymentPage, setPaymentPage] = useState(1);

  const monthRange = useMemo(() => getCurrentMonthRange(), []);
  const kpiPeriodLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleString('es-DO', { month: 'long', year: 'numeric' });
  }, []);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'warning', onConfirm: () => {} });

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successPayment, setSuccessPayment] = useState<PaymentSuccessData | null>(null);
  const [successCreditMinor, setSuccessCreditMinor] = useState(0);
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('desc');

  const PAGE_SIZE = 25;
  const lockedClientId = selectedClient ? String(selectedClient.id) : undefined;

  /* ── Data queries ── */

  const { data: clientsData } = useQuery<{ data: { data: Client[] } }>(
    'clients',
    clientsAPI.getAll,
    { staleTime: 60_000 }
  );
  const clients: Client[] = clientsData?.data?.data || [];

  const { data: billingConfigData } = useQuery('fiscal-billing', fiscalAPI.getBilling, {
    staleTime: 60_000,
    enabled: hasPermission('configuration.read'),
  });
  const billingConfig = billingConfigData?.data?.data as {
    autoGenerateInvoices?: boolean;
    billingDay?: number;
    billingHour?: number;
  } | undefined;
  const monthlyScheduleText = useMemo(
    () =>
      formatMonthlyInvoiceSchedule(
        billingConfig
          ? {
              monthlyInvoiceAutoEnabled: billingConfig.autoGenerateInvoices,
              monthlyInvoiceGenerationDay: billingConfig.billingDay,
              monthlyInvoiceGenerationHour: billingConfig.billingHour,
            }
          : null
      ),
    [billingConfig]
  );

  const changeTab = (tab: ActiveTab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'payments' || tabParam === 'pagos') {
      setActiveTab('pagos');
    } else if (tabParam === 'invoices' || tabParam === 'facturas') {
      setActiveTab('facturas');
    } else if (tabParam === 'pendiente' || tabParam === 'pending') {
      setActiveTab('pendiente');
    }
  }, [searchParams]);

  useEffect(() => {
    const clientIdParam = searchParams.get('clientId');
    if (!clientIdParam || clients.length === 0) return;
    const match = clients.find(c => String(c.id) === clientIdParam);
    if (match) setSelectedClient(match);
  }, [searchParams, clients]);

  const { data: pendingData, isLoading: pendingLoading } = useQuery<{
    data: { data: Invoice[]; total: number; page: number; totalPages: number };
  }>(
    [
      'cobros-pending-invoices',
      lockedClientId,
      pendingFilterStatus,
      pendingFilterStart,
      pendingFilterEnd,
      pendingPage,
    ],
    () =>
      invoicesAPI.getAll({
        clientId: lockedClientId || undefined,
        unpaid: true,
        status: pendingFilterStatus || undefined,
        startDate: pendingFilterStart || undefined,
        endDate: pendingFilterEnd || undefined,
        page: pendingPage,
        limit: PAGE_SIZE,
      }),
    { keepPreviousData: true }
  );

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery<{
    data: { data: Invoice[]; total: number; page: number; totalPages: number };
  }>(
    [
      'cobros-invoices',
      lockedClientId,
      invoiceFilterStatus,
      invoiceFilterStart,
      invoiceFilterEnd,
      invoicePage,
    ],
    () =>
      invoicesAPI.getAll({
        clientId: lockedClientId || undefined,
        status: invoiceFilterStatus || undefined,
        startDate: invoiceFilterStart || undefined,
        endDate: invoiceFilterEnd || undefined,
        page: invoicePage,
        limit: PAGE_SIZE,
      }),
    { keepPreviousData: true }
  );

  const { data: paymentsData, isLoading: paymentsLoading } = useQuery<{
    data: { data: Payment[]; total: number; page: number; totalPages: number };
  }>(
    [
      'cobros-payments',
      lockedClientId,
      paymentFilterMethod,
      paymentFilterStatus,
      paymentFilterStart,
      paymentFilterEnd,
      paymentPage,
    ],
    () =>
      paymentsAPI.getAll({
        clientId: lockedClientId || undefined,
        paymentMethod: paymentFilterMethod || undefined,
        status: paymentFilterStatus || undefined,
        startDate: paymentFilterStart || undefined,
        endDate: paymentFilterEnd || undefined,
        page: paymentPage,
        limit: PAGE_SIZE,
      }),
    { keepPreviousData: true }
  );

  // Pending invoices for the client summary card
  const { data: pendingInvoicesData } = useQuery<{ data: { data: Invoice[] } }>(
    ['pending-invoices-summary', lockedClientId],
    () => paymentsAPI.getPendingInvoices(parseInt(lockedClientId!)),
    { enabled: Boolean(lockedClientId), staleTime: 30_000 }
  );
  const pendingInvoices: Invoice[] = pendingInvoicesData?.data?.data || [];

  const { data: invoiceStatsRes, isLoading: invoiceStatsLoading } = useQuery(
    ['cobros-invoice-stats', lockedClientId],
    () =>
      invoicesAPI.getStats({
        clientId: lockedClientId || undefined,
      }),
    { staleTime: 30_000 }
  );

  const { data: paymentStatsRes, isLoading: paymentStatsLoading } = useQuery(
    ['cobros-payment-stats', lockedClientId, monthRange.start, monthRange.end],
    () =>
      paymentsAPI.getStats({
        clientId: lockedClientId || undefined,
        startDate: monthRange.start,
        endDate: monthRange.end,
      }),
    { staleTime: 30_000 }
  );

  const invoiceStats = (invoiceStatsRes as any)?.data?.data || (invoiceStatsRes as any)?.data;
  const paymentStats = (paymentStatsRes as any)?.data?.data || (paymentStatsRes as any)?.data;

  const totalPorCobrar = Number(invoiceStats?.totalBalanceDue ?? 0);
  const vencido = useMemo(() => {
    const rows = invoiceStats?.invoicesByStatus || [];
    const overdue = rows.find((r: any) => r.status === 'OVERDUE');
    const minor = overdue?._sum?.balanceDueMinor;
    if (minor != null) return Number(minor) / 100;
    return 0;
  }, [invoiceStats]);
  const pendiente = Math.max(totalPorCobrar - vencido, 0);
  const cobradoPeriodo = Number(paymentStats?.totalRevenue ?? 0);

  /* ── Panel helpers ── */

  const closePanel = () => {
    setPanelMode(null);
    setSelectedInvoice(null);
    setSelectedPayment(null);
    setQuickPayInvoice(null);
    setDuplicateInvoice(null);
  };

  const openInvoiceCreate = () => {
    setSelectedInvoice(null);
    setDuplicateInvoice(null);
    setQuickPayInvoice(null);
    changeTab('facturas');
    setPanelMode('invoice');
  };

  const openPaymentCreate = () => {
    setSelectedPayment(null);
    setQuickPayInvoice(null);
    changeTab('pagos');
    setPanelMode('payment');
  };

  const openInvoiceEdit = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDuplicateInvoice(null);
    setQuickPayInvoice(null);
    changeTab('facturas');
    setPanelMode('invoice');
  };

  const openInvoiceDuplicate = (invoice: Invoice) => {
    setSelectedInvoice(null);
    setDuplicateInvoice(invoice);
    setQuickPayInvoice(null);
    changeTab('facturas');
    setPanelMode('invoice');
  };

  const openPaymentEdit = (payment: Payment) => {
    setSelectedPayment(payment);
    setQuickPayInvoice(null);
    changeTab('pagos');
    setPanelMode('payment');
  };

  const openQuickPay = (invoice: Invoice) => {
    const clientFromInvoice =
      (invoice.client as Client | undefined) ||
      clients.find(c => String(c.id) === String(invoice.clientId));
    if (clientFromInvoice) {
      setSelectedClient(clientFromInvoice);
    }
    setSelectedPayment(null);
    setSelectedInvoice(null);
    setDuplicateInvoice(null);
    setQuickPayInvoice(invoice);
    setPanelMode('payment');
  };

  /* ── Client selector ── */

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setInvoicePage(1);
    setPendingPage(1);
    setPaymentPage(1);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setInvoicePage(1);
    setPendingPage(1);
    setPaymentPage(1);
  };

  /* ── Invoice mutations ── */

  const createInvoiceMutation = useMutation((data: Record<string, unknown>) => invoicesAPI.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('cobros-invoices');
      queryClient.invalidateQueries('cobros-pending-invoices');
      queryClient.invalidateQueries('pending-invoices-summary');
      queryClient.invalidateQueries('cobros-invoice-stats');
      queryClient.invalidateQueries('cobros-payment-stats');
      toast.success('Factura creada exitosamente');
      closePanel();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear la factura');
    },
  });

  const updateInvoiceMutation = useMutation(
    ({ id, data }: { id: number; data: Record<string, unknown> }) => invoicesAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cobros-invoices');
        queryClient.invalidateQueries('cobros-pending-invoices');
        queryClient.invalidateQueries('pending-invoices-summary');
        queryClient.invalidateQueries('cobros-invoice-stats');
        toast.success('Factura actualizada exitosamente');
        closePanel();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Error al actualizar la factura');
      },
    }
  );

  const deleteInvoiceMutation = useMutation((id: number) => invoicesAPI.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('cobros-invoices');
      queryClient.invalidateQueries('cobros-pending-invoices');
      queryClient.invalidateQueries('pending-invoices-summary');
      queryClient.invalidateQueries('cobros-invoice-stats');
      toast.success('Factura eliminada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar la factura');
    },
  });

  const markAsPaidMutation = useMutation((id: number) => invoicesAPI.markAsPaid(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('cobros-invoices');
      queryClient.invalidateQueries('cobros-pending-invoices');
      queryClient.invalidateQueries('pending-invoices-summary');
      queryClient.invalidateQueries('cobros-payments');
      queryClient.invalidateQueries('cobros-invoice-stats');
      queryClient.invalidateQueries('cobros-payment-stats');
      toast.success('Factura marcada como pagada');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar la factura');
    },
  });

  const generateMonthlyMutation = useMutation(() => invoicesAPI.generateMonthly(), {
    onSuccess: (response: any) => {
      queryClient.invalidateQueries('cobros-invoices');
      queryClient.invalidateQueries('cobros-pending-invoices');
      queryClient.invalidateQueries('pending-invoices-summary');
      queryClient.invalidateQueries('cobros-invoice-stats');
      toast.success(`${response.data.generatedCount} facturas mensuales generadas`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al generar facturas mensuales');
    },
  });

  const handleSaveInvoice = (data: Record<string, unknown>) => {
    if (selectedInvoice) {
      updateInvoiceMutation.mutate({ id: selectedInvoice.id, data });
    } else {
      createInvoiceMutation.mutate(data);
    }
  };

  const handleDeleteInvoice = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar factura',
      message: '¿Está seguro de que desea eliminar esta factura? Esta acción no se puede deshacer.',
      type: 'danger',
      onConfirm: () => {
        deleteInvoiceMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleMarkAsPaid = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Marcar como pagada',
      message: '¿Confirma marcar esta factura como pagada?',
      type: 'warning',
      onConfirm: () => {
        markAsPaidMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleGenerateMonthly = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Generar facturas mensuales',
      message:
        `¿Desea generar ahora las facturas mensuales de todos los planes activos? ${monthlyScheduleText} Las que ya existen para este mes se omiten.`,
      type: 'info',
      onConfirm: () => {
        generateMonthlyMutation.mutate();
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleViewInvoicePDF = async (invoiceId: number) => {
    try {
      const response = await invoicesAPI.viewPDF(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (error: any) {
      toast.error('Error al abrir PDF: ' + (error.response?.data?.error || error.message));
    }
  };

  /* ── Payment mutations ── */

  const createPaymentMutation = useMutation(
    (data: Record<string, unknown>) => paymentsAPI.create(data),
    {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries('cobros-payments');
        queryClient.invalidateQueries('cobros-invoices');
        queryClient.invalidateQueries('cobros-pending-invoices');
        queryClient.invalidateQueries('pending-invoices-summary');
        queryClient.invalidateQueries('cobros-invoice-stats');
        queryClient.invalidateQueries('cobros-payment-stats');
        toast.success('Pago registrado exitosamente');
        const warnings: string[] = response?.data?.warnings || [];
        warnings.forEach(w => toast(w, { icon: '⚠️', duration: 6000 }));
        const result = response?.data?.data;
        const payment = result?.payment || null;
        if (payment) {
          setSuccessPayment(payment);
          setSuccessCreditMinor(result?.creditBalanceMinor || 0);
          setSuccessModalOpen(true);
        }
        closePanel();
      },
      onError: (error: any) => {
        toast.error('Error al registrar el pago: ' + (error.response?.data?.error || error.message));
      },
    }
  );

  const updatePaymentMutation = useMutation(
    ({ id, data }: { id: number; data: Record<string, unknown> }) => paymentsAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cobros-payments');
        toast.success('Pago actualizado exitosamente');
        closePanel();
      },
      onError: (error: any) => {
        toast.error('Error al actualizar el pago: ' + (error.response?.data?.error || error.message));
      },
    }
  );

  const deletePaymentMutation = useMutation((id: number) => paymentsAPI.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('cobros-payments');
      queryClient.invalidateQueries('cobros-invoices');
      queryClient.invalidateQueries('cobros-pending-invoices');
      queryClient.invalidateQueries('pending-invoices-summary');
      queryClient.invalidateQueries('cobros-invoice-stats');
      queryClient.invalidateQueries('cobros-payment-stats');
      toast.success('Pago anulado exitosamente');
    },
    onError: (error: any) => {
      toast.error('Error al anular el pago: ' + (error.response?.data?.error || error.message));
    },
  });

  const verifyPaymentMutation = useMutation((id: number) => paymentsAPI.verify(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('cobros-payments');
      toast.success('Pago verificado exitosamente');
    },
    onError: (error: any) => {
      toast.error('Error al verificar el pago: ' + (error.response?.data?.error || error.message));
    },
  });

  const handleSavePayment = (data: Record<string, unknown>) => {
    if (selectedPayment) {
      updatePaymentMutation.mutate({ id: selectedPayment.id, data });
    } else {
      createPaymentMutation.mutate(data);
    }
  };

  const handleDeletePayment = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Anular Pago',
      message: '¿Está seguro de que desea anular este pago? Las facturas asociadas serán revertidas.',
      type: 'danger',
      onConfirm: () => {
        deletePaymentMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleVerifyPayment = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Verificar Pago',
      message: '¿Confirma la verificación de este pago? Una vez verificado no podrá modificarse.',
      type: 'warning',
      onConfirm: () => {
        verifyPaymentMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleDownloadReceipt = async (id: number) => {
    try {
      const response = await paymentsAPI.downloadReceipt(id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `recibo_pago_${id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo generar el recibo PDF');
    }
  };

  /* ── Invoice table columns ── */

  const invoiceColumns: TableColumn<Invoice>[] = [
    {
      key: 'invoiceNumber' as keyof Invoice,
      label: 'N° / Cliente',
      render: (value, invoice) => (
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {(value as string) || `#${invoice.id}`}
          </p>
          <p className="text-xs text-slate-500">
            {invoice.client?.name || 'N/A'} · {invoice.client?.cedula || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'amountDue' as keyof Invoice,
      label: 'Total',
      render: value => (
        <span className="text-sm font-semibold text-slate-900">
          {formatMoney(Number(value))}
        </span>
      ),
    },
    {
      key: 'balanceDueMinor' as keyof Invoice,
      label: 'Saldo',
      render: (_, invoice) => {
        const bal = getInvoiceBalance(invoice);
        return (
          <span className={`text-sm font-semibold ${bal > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
            {formatMoney(bal)}
          </span>
        );
      },
    },
    {
      key: 'dueDate' as keyof Invoice,
      label: 'Vencimiento',
      sortable: true,
      render: value => (
        <div className="flex items-center gap-1 text-sm text-slate-700">
          <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
          {new Date(value as string).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'status' as keyof Invoice,
      label: 'Estado',
      render: (_, invoice) => (
        <ModernBadge className={invoiceStatusBadgeClass(invoice.status)}>
          {invoiceStatusLabel(invoice.status)}
        </ModernBadge>
      ),
    },
    {
      key: 'id' as keyof Invoice,
      label: 'Acciones',
      className: 'text-right',
      render: (_, invoice) => {
        const bal = getInvoiceBalance(invoice);
        const canCollect =
          bal > 0 &&
          (invoice.status === 'ISSUED' ||
            invoice.status === 'PARTIAL' ||
            invoice.status === 'OVERDUE' ||
            invoice.status === 'PENDING');

        const canEdit = invoice.status === 'DRAFT' || invoice.status === 'ISSUED';
        const canMarkPaid =
          invoice.status === 'ISSUED' ||
          invoice.status === 'PARTIAL' ||
          invoice.status === 'OVERDUE';

        return (
          <div className="flex items-center justify-end gap-2">
            <PermissionGate permission="payments.create">
              {canCollect ? (
                <button
                  type="button"
                  onClick={() => openQuickPay(invoice)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white transition-colors duration-200 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                >
                  <BanknotesIcon className="h-3.5 w-3.5" />
                  Cobrar
                </button>
              ) : null}
            </PermissionGate>

            <RowActionsMenu
              items={[
                {
                  label: 'Ver',
                  onClick: () => navigate(`/invoices/${invoice.id}`),
                },
                {
                  label: 'Editar',
                  onClick: () => openInvoiceEdit(invoice),
                  hidden: !canEdit || !hasPermission('invoices.update'),
                },
                {
                  label: 'Duplicar',
                  onClick: () => openInvoiceDuplicate(invoice),
                  hidden: !hasPermission('invoices.create'),
                },
                {
                  label: 'Ver PDF',
                  onClick: () => handleViewInvoicePDF(invoice.id),
                  hidden: !hasPermission('invoices.generate_pdf'),
                },
                {
                  label: 'Marcar como pagada',
                  onClick: () => handleMarkAsPaid(invoice.id),
                  hidden: !canMarkPaid || !hasPermission('invoices.update'),
                },
                {
                  label: 'Eliminar',
                  onClick: () => handleDeleteInvoice(invoice.id),
                  tone: 'danger',
                  hidden: !hasPermission('invoices.delete'),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  /* ── Payment table columns ── */

  const paymentColumns: TableColumn<Payment>[] = [
    {
      key: 'client' as keyof Payment,
      label: 'Cliente',
      render: (_, payment) => (
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
            <CreditCardIcon className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">
              {payment.client?.name || (payment.clientId ? `Cliente #${payment.clientId}` : 'N/A')}
            </p>
            <p className="text-xs text-slate-500">{payment.client?.cedula || '—'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'amountMinor' as keyof Payment,
      label: 'Monto',
      sortable: true,
      render: (_, payment) => (
        <span className="text-sm font-bold text-slate-900">
          ${(payment.amountMinor != null
            ? payment.amountMinor / 100
            : Number(payment.amount || 0)
          ).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      key: 'paymentMethod' as keyof Payment,
      label: 'Método',
      render: (_, payment) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            METHOD_BADGE_COLORS[payment.paymentMethod] || 'bg-slate-100 text-slate-700'
          }`}
        >
          {METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod}
        </span>
      ),
    },
    {
      key: 'paymentDate' as keyof Payment,
      label: 'Fecha',
      sortable: true,
      render: value => (
        <div className="flex items-center gap-1 text-sm text-slate-700">
          <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
          {new Date(value as string).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'allocations' as keyof Payment,
      label: 'Facturas',
      render: (_, payment) => {
        const count = payment.allocations?.length || 0;
        return (
          <span className={`text-sm font-medium ${count > 0 ? 'text-indigo-700' : 'text-slate-400'}`}>
            {count > 0 ? count : '—'}
          </span>
        );
      },
    },
    {
      key: 'status' as keyof Payment,
      label: 'Estado',
      render: (_, payment) => {
        const status = payment.status || (payment.isVerified ? 'CONFIRMED' : 'PENDING');
        return (
          <ModernBadge className={paymentStatusBadgeClass(status)}>
            {PAYMENT_STATUS_LABELS[status] || status}
          </ModernBadge>
        );
      },
    },
    {
      key: 'id' as keyof Payment,
      label: 'Acciones',
      className: 'text-right',
      render: (_, payment) => {
        const canEdit = payment.status === 'PENDING';
        const canVerify =
          (payment.status === 'PENDING' || (!payment.status && !payment.isVerified)) &&
          payment.paymentMethod !== 'EFECTIVO' &&
          payment.paymentMethod !== 'CHEQUE';
        const canDelete = payment.status !== 'CANCELLED' && payment.status !== 'REFUNDED';

        return (
          <div className="flex items-center justify-end">
            <RowActionsMenu
              items={[
                {
                  label: 'Ver',
                  onClick: () => navigate(`/payments/${payment.id}`),
                },
                {
                  label: 'Editar',
                  onClick: () => openPaymentEdit(payment),
                  hidden: !canEdit || !hasPermission('payments.update'),
                },
                {
                  label: 'Descargar recibo',
                  onClick: () => handleDownloadReceipt(payment.id),
                },
                {
                  label: 'Verificar',
                  onClick: () => handleVerifyPayment(payment.id),
                  hidden: !canVerify || !hasPermission('payments.verify'),
                },
                {
                  label: 'Eliminar',
                  onClick: () => handleDeletePayment(payment.id),
                  tone: 'danger',
                  hidden: !canDelete || !hasPermission('payments.delete'),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  /* ── Render ── */

  const isPanelOpen = panelMode !== null;
  const invoices = invoicesData?.data?.data || [];
  const pendingInvoicesList = pendingData?.data?.data || [];
  const payments = paymentsData?.data?.data || [];

  const invoiceHasFilters = !!(invoiceFilterStatus || invoiceFilterStart || invoiceFilterEnd);
  const pendingHasFilters = !!(pendingFilterStatus || pendingFilterStart || pendingFilterEnd);
  const paymentHasFilters = !!(
    paymentFilterMethod ||
    paymentFilterStatus ||
    paymentFilterStart ||
    paymentFilterEnd
  );

  const sortInvoicesByDueDate = (list: Invoice[]) =>
    [...list].sort((a, b) => {
      const da = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      const db = b.dueDate ? new Date(b.dueDate).getTime() : 0;
      return listSortDir === 'asc' ? da - db : db - da;
    });

  const sortPaymentsByDate = (list: Payment[]) =>
    [...list].sort((a, b) => {
      const da = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
      const db = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
      return listSortDir === 'asc' ? da - db : db - da;
    });

  const sortedPendingInvoices = sortInvoicesByDueDate(pendingInvoicesList);
  const sortedInvoices = sortInvoicesByDueDate(invoices);
  const sortedPayments = sortPaymentsByDate(payments);

  const activeFilterCount =
    activeTab === 'pendiente'
      ? [pendingFilterStatus, pendingFilterStart, pendingFilterEnd].filter(Boolean).length
      : activeTab === 'facturas'
        ? [invoiceFilterStatus, invoiceFilterStart, invoiceFilterEnd].filter(Boolean).length
        : [paymentFilterMethod, paymentFilterStatus, paymentFilterStart, paymentFilterEnd].filter(
            Boolean
          ).length;

  const invoiceCardProps = (invoice: Invoice, showCollect = true) => {
    const bal = getInvoiceBalance(invoice);
    const canCollect =
      bal > 0 &&
      (invoice.status === 'ISSUED' ||
        invoice.status === 'PARTIAL' ||
        invoice.status === 'OVERDUE' ||
        invoice.status === 'PENDING');
    const canEdit = invoice.status === 'DRAFT' || invoice.status === 'ISSUED';
    const canMarkPaid =
      invoice.status === 'ISSUED' ||
      invoice.status === 'PARTIAL' ||
      invoice.status === 'OVERDUE';

    return {
      invoice,
      formatMoney,
      getBalance: getInvoiceBalance,
      statusLabel: invoiceStatusLabel,
      statusClass: invoiceStatusBadgeClass,
      onCollect: showCollect && canCollect ? () => openQuickPay(invoice) : undefined,
      onView: () => navigate(`/invoices/${invoice.id}`),
      onEdit:
        canEdit && hasPermission('invoices.update') ? () => openInvoiceEdit(invoice) : undefined,
      onDuplicate: hasPermission('invoices.create')
        ? () => openInvoiceDuplicate(invoice)
        : undefined,
      onPdf: hasPermission('invoices.generate_pdf')
        ? () => handleViewInvoicePDF(invoice.id)
        : undefined,
      onMarkPaid:
        canMarkPaid && hasPermission('invoices.update')
          ? () => handleMarkAsPaid(invoice.id)
          : undefined,
      onDelete: hasPermission('invoices.delete')
        ? () => handleDeleteInvoice(invoice.id)
        : undefined,
      canCollect,
      canEdit: canEdit && hasPermission('invoices.update'),
      canMarkPaid: canMarkPaid && hasPermission('invoices.update'),
      showCollect,
    };
  };

  const tabDefs = [
    { id: 'pendiente' as const, label: 'Pendientes', count: pendingData?.data?.total },
    { id: 'facturas' as const, label: 'Facturas', count: invoicesData?.data?.total },
    { id: 'pagos' as const, label: 'Pagos', count: paymentsData?.data?.total },
  ];

  const activeTabTotal =
    activeTab === 'pendiente'
      ? pendingData?.data?.total ?? sortedPendingInvoices.length
      : activeTab === 'facturas'
        ? invoicesData?.data?.total ?? sortedInvoices.length
        : paymentsData?.data?.total ?? sortedPayments.length;

  const invoiceStatusFilterOptions = [{ value: '', label: 'Todos' }, ...INVOICE_STATUS_OPTIONS];

  const paymentStatusFilterOptions = [
    { value: '', label: 'Todos' },
    { value: 'PENDING', label: 'Pendiente' },
    { value: 'CONFIRMED', label: 'Confirmado' },
    { value: 'CANCELLED', label: 'Anulado' },
    { value: 'REFUNDED', label: 'Reembolsado' },
    { value: 'PARTIAL_REFUND', label: 'Reemb. parcial' },
  ];

  const clearActiveTabFilters = () => {
    if (activeTab === 'pendiente') {
      setPendingFilterStatus('');
      setPendingFilterStart('');
      setPendingFilterEnd('');
      setPendingPage(1);
    } else if (activeTab === 'facturas') {
      setInvoiceFilterStatus('');
      setInvoiceFilterStart('');
      setInvoiceFilterEnd('');
      setInvoicePage(1);
    } else {
      setPaymentFilterMethod('');
      setPaymentFilterStatus('');
      setPaymentFilterStart('');
      setPaymentFilterEnd('');
      setPaymentPage(1);
    }
    setListSortDir('desc');
  };

  return (
    <div className="min-h-full space-y-6 bg-[#F6F8FC] animate-fade-in-up md:space-y-8">
      {/* Desktop header */}
      <header className="hidden flex-col gap-4 pb-2 md:flex sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Cobros</h1>
          <p className="mt-1 text-sm text-slate-500">
            Gestión unificada de facturas y pagos. {monthlyScheduleText}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <PermissionGate permission="invoices.create">
            <Button onClick={handleGenerateMonthly} variant="outline" size="md">
              <CalendarIcon className="mr-2 h-4 w-4" />
              Generar mensual
            </Button>
          </PermissionGate>
          <PermissionGate permission="invoices.create">
            <Button onClick={openInvoiceCreate} variant="outline" size="md">
              <DocumentTextIcon className="mr-2 h-4 w-4" />
              Nueva Factura
            </Button>
          </PermissionGate>
          <PermissionGate permission="payments.create">
            <Button
              onClick={openPaymentCreate}
              className={`text-white transition-all duration-200 ${
                isPanelOpen && panelMode === 'payment' && !selectedPayment
                  ? 'bg-indigo-700 ring-2 ring-indigo-300'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
              size="md"
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              Nuevo Pago
            </Button>
          </PermissionGate>
        </div>
      </header>

      {/* Mobile header */}
      <div className="flex items-start justify-between gap-3 md:hidden">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Cobros</h1>
          <p className="mt-1 text-sm text-slate-500">
            Facturas y pagos. {monthlyScheduleText}
          </p>
        </div>
        <button
          type="button"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5EAF1] bg-white text-slate-600 shadow-sm transition active:scale-95"
          aria-label="Notificaciones"
        >
          <BellIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile quick actions */}
      <div className="grid grid-cols-2 gap-2 md:hidden">
        <PermissionGate permission="invoices.create">
          <button
            type="button"
            onClick={handleGenerateMonthly}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-[#E5EAF1] bg-white text-sm font-semibold text-slate-800 shadow-sm transition active:scale-[0.98]"
          >
            <CalendarIcon className="h-4 w-4 text-primary-600" />
            Mensual
          </button>
        </PermissionGate>
        <PermissionGate permission="invoices.create">
          <button
            type="button"
            onClick={openInvoiceCreate}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-[#E5EAF1] bg-white text-sm font-semibold text-slate-800 shadow-sm transition active:scale-[0.98]"
          >
            <DocumentTextIcon className="h-4 w-4 text-primary-600" />
            Nueva factura
          </button>
        </PermissionGate>
        <PermissionGate permission="payments.create">
          <button
            type="button"
            onClick={openPaymentCreate}
            className="col-span-2 inline-flex h-11 items-center justify-center gap-1.5 rounded-xl bg-primary-600 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            Nuevo pago
          </button>
        </PermissionGate>
      </div>

      <ClientSearchBar
        clients={clients}
        selectedClient={selectedClient}
        pendingInvoices={pendingInvoices}
        onSelect={handleSelectClient}
        onClear={handleClearClient}
        navigate={navigate}
      />

      <CobrosKpiStrip
        totalPorCobrar={totalPorCobrar}
        vencido={vencido}
        pendiente={pendiente}
        cobradoPeriodo={cobradoPeriodo}
        periodLabel={kpiPeriodLabel}
        loading={invoiceStatsLoading || paymentStatsLoading}
      />

      {isPanelOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={closePanel}
          aria-hidden="true"
        />
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
        <div className="min-w-0 flex-1 space-y-4 md:space-y-6">
          {/* Mobile segmented tabs */}
          <div className="flex rounded-xl bg-slate-100 p-1 md:hidden">
            {tabDefs.map(tab => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => changeTab(tab.id)}
                  className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs font-semibold transition active:scale-[0.98] ${
                    active
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span className="truncate">{tab.label}</span>
                  {tab.count != null && (
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                        active ? 'bg-primary-50 text-primary-700' : 'bg-slate-200/80 text-slate-600'
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Desktop pill tabs */}
          <div className="hidden flex-wrap gap-2 md:flex">
            {tabDefs.map(tab => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => changeTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
                    active
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 ring-1 ring-gray-200 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                  {tab.count != null && (
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                        active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Mobile results bar */}
          <div className="flex items-center justify-between border-b border-[#E5EAF1] pb-2 md:hidden">
            <p className="text-sm font-medium text-slate-600">
              <span className="tabular-nums font-semibold text-slate-900">{activeTabTotal}</span>{' '}
              resultados
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setFiltersSheetOpen(true)}
                className="relative inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 active:scale-95"
              >
                <FunnelIcon className="h-4 w-4" />
                Filtros
                {activeFilterCount > 0 ? (
                  <span className="ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-bold text-white">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => setFiltersSheetOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 active:scale-95"
              >
                <ArrowsUpDownIcon className="h-4 w-4" />
                Ordenar
              </button>
            </div>
          </div>

          {activeTab === 'pendiente' && (
            <div className="space-y-4 md:space-y-6">
              <div className="hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:block">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <div className="min-w-[140px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Estado</label>
                    <select
                      value={pendingFilterStatus}
                      onChange={e => {
                        setPendingFilterStatus(e.target.value);
                        setPendingPage(1);
                      }}
                      className={filterInputCls}
                      aria-label="Filtrar pendientes por estado"
                    >
                      {PENDING_STATUS_FILTERS.map(opt => (
                        <option key={opt.value || 'all'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[150px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Desde</label>
                    <input
                      type="date"
                      value={pendingFilterStart}
                      onChange={e => {
                        setPendingFilterStart(e.target.value);
                        setPendingPage(1);
                      }}
                      aria-label="Pendientes desde"
                      className={filterInputCls}
                    />
                  </div>
                  <div className="min-w-[150px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Hasta</label>
                    <input
                      type="date"
                      value={pendingFilterEnd}
                      onChange={e => {
                        setPendingFilterEnd(e.target.value);
                        setPendingPage(1);
                      }}
                      aria-label="Pendientes hasta"
                      className={filterInputCls}
                    />
                  </div>
                  <div className="min-w-[160px] flex-1">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Cliente</label>
                    <div className="flex h-9 items-center rounded-lg border border-gray-200 bg-slate-50 px-2.5 text-sm text-slate-600">
                      {selectedClient ? clientFullName(selectedClient) : 'Todos'}
                    </div>
                  </div>
                  {pendingHasFilters && (
                    <button
                      type="button"
                      onClick={() => {
                        setPendingFilterStatus('');
                        setPendingFilterStart('');
                        setPendingFilterEnd('');
                        setPendingPage(1);
                      }}
                      className="mt-auto inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium text-red-500 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2.5 md:hidden">
                {pendingLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-[140px] animate-pulse rounded-2xl bg-slate-100" />
                  ))
                ) : sortedPendingInvoices.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center">
                    <DocumentTextIcon className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">
                      No hay facturas pendientes
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Ajuste los filtros o registre un nuevo cobro.
                    </p>
                  </div>
                ) : (
                  sortedPendingInvoices.map(invoice => (
                    <CobrosMobileInvoiceCard
                      key={invoice.id}
                      {...invoiceCardProps(invoice, true)}
                    />
                  ))
                )}
              </div>

              <div className="hidden md:block">
                <DataTableCard>
                  <Table
                    data={sortedPendingInvoices}
                    columns={invoiceColumns}
                    loading={pendingLoading}
                    emptyMessage="No hay facturas pendientes de cobro"
                    rowClassName="hover:bg-slate-50/80 transition-colors duration-200"
                  />
                </DataTableCard>
              </div>

              {pendingData?.data?.totalPages != null && pendingData.data.totalPages > 1 && (
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs text-slate-500">
                    Página {pendingData.data.page} de {pendingData.data.totalPages} —{' '}
                    {pendingData.data.total} pendientes
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPendingPage(p => Math.max(1, p - 1))}
                      disabled={pendingPage <= 1}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      ← Anterior
                    </button>
                    <span className="px-2 text-xs text-slate-600">
                      {pendingPage} / {pendingData.data.totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPendingPage(p => Math.min(pendingData.data.totalPages, p + 1))
                      }
                      disabled={pendingPage >= pendingData.data.totalPages}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Siguiente →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'facturas' && (
            <div className="space-y-4 md:space-y-6">
              <div className="hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:block">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <div className="min-w-[140px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Estado</label>
                    <select
                      value={invoiceFilterStatus}
                      onChange={e => {
                        setInvoiceFilterStatus(e.target.value);
                        setInvoicePage(1);
                      }}
                      className={filterInputCls}
                      aria-label="Filtrar facturas por estado"
                    >
                      <option value="">Todos</option>
                      {INVOICE_STATUS_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[150px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Desde</label>
                    <input
                      type="date"
                      value={invoiceFilterStart}
                      onChange={e => {
                        setInvoiceFilterStart(e.target.value);
                        setInvoicePage(1);
                      }}
                      aria-label="Facturas desde"
                      className={filterInputCls}
                    />
                  </div>
                  <div className="min-w-[150px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Hasta</label>
                    <input
                      type="date"
                      value={invoiceFilterEnd}
                      onChange={e => {
                        setInvoiceFilterEnd(e.target.value);
                        setInvoicePage(1);
                      }}
                      aria-label="Facturas hasta"
                      className={filterInputCls}
                    />
                  </div>
                  <div className="min-w-[160px] flex-1">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Cliente</label>
                    <div className="flex h-9 items-center rounded-lg border border-gray-200 bg-slate-50 px-2.5 text-sm text-slate-600">
                      {selectedClient ? clientFullName(selectedClient) : 'Todos'}
                    </div>
                  </div>
                  {invoiceHasFilters && (
                    <button
                      type="button"
                      onClick={() => {
                        setInvoiceFilterStatus('');
                        setInvoiceFilterStart('');
                        setInvoiceFilterEnd('');
                        setInvoicePage(1);
                      }}
                      className="mt-auto inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium text-red-500 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2.5 md:hidden">
                {invoicesLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-[140px] animate-pulse rounded-2xl bg-slate-100" />
                  ))
                ) : sortedInvoices.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center">
                    <DocumentTextIcon className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">Sin facturas</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Ajuste los filtros o cree una nueva factura.
                    </p>
                  </div>
                ) : (
                  sortedInvoices.map(invoice => (
                    <CobrosMobileInvoiceCard
                      key={invoice.id}
                      {...invoiceCardProps(invoice, true)}
                    />
                  ))
                )}
              </div>

              <div className="hidden md:block">
                <DataTableCard>
                  <Table
                    data={sortedInvoices}
                    columns={invoiceColumns}
                    loading={invoicesLoading}
                    emptyMessage="No se encontraron facturas"
                    rowClassName="hover:bg-slate-50/80 transition-colors duration-200"
                  />
                </DataTableCard>
              </div>

              {invoicesData?.data?.totalPages != null && invoicesData.data.totalPages > 1 && (
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs text-slate-500">
                    Página {invoicesData.data.page} de {invoicesData.data.totalPages} —{' '}
                    {invoicesData.data.total} facturas en total
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                      disabled={invoicePage <= 1}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      ← Anterior
                    </button>
                    <span className="px-2 text-xs text-slate-600">
                      {invoicePage} / {invoicesData.data.totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setInvoicePage(p => Math.min(invoicesData.data.totalPages, p + 1))
                      }
                      disabled={invoicePage >= invoicesData.data.totalPages}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Siguiente →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'pagos' && (
            <div className="space-y-4 md:space-y-6">
              <div className="hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:block">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <div className="min-w-[140px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Estado</label>
                    <select
                      value={paymentFilterStatus}
                      onChange={e => {
                        setPaymentFilterStatus(e.target.value);
                        setPaymentPage(1);
                      }}
                      className={filterInputCls}
                      aria-label="Filtrar pagos por estado"
                    >
                      {paymentStatusFilterOptions.map(opt => (
                        <option key={opt.value || 'all'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[150px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Desde</label>
                    <input
                      type="date"
                      value={paymentFilterStart}
                      onChange={e => {
                        setPaymentFilterStart(e.target.value);
                        setPaymentPage(1);
                      }}
                      aria-label="Pagos desde"
                      className={filterInputCls}
                    />
                  </div>
                  <div className="min-w-[150px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Hasta</label>
                    <input
                      type="date"
                      value={paymentFilterEnd}
                      onChange={e => {
                        setPaymentFilterEnd(e.target.value);
                        setPaymentPage(1);
                      }}
                      aria-label="Pagos hasta"
                      className={filterInputCls}
                    />
                  </div>
                  <div className="min-w-[160px]">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Método</label>
                    <select
                      value={paymentFilterMethod}
                      onChange={e => {
                        setPaymentFilterMethod(e.target.value);
                        setPaymentPage(1);
                      }}
                      className={filterInputCls}
                      aria-label="Filtrar pagos por método"
                    >
                      <option value="">Todos</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[140px] flex-1">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Cliente</label>
                    <div className="flex h-9 items-center rounded-lg border border-gray-200 bg-slate-50 px-2.5 text-sm text-slate-600">
                      {selectedClient ? clientFullName(selectedClient) : 'Todos'}
                    </div>
                  </div>
                  {paymentHasFilters && (
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentFilterMethod('');
                        setPaymentFilterStatus('');
                        setPaymentFilterStart('');
                        setPaymentFilterEnd('');
                        setPaymentPage(1);
                      }}
                      className="mt-auto inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium text-red-500 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2.5 md:hidden">
                {paymentsLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-[130px] animate-pulse rounded-2xl bg-slate-100" />
                  ))
                ) : sortedPayments.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center">
                    <CreditCardIcon className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">Sin pagos</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Ajuste los filtros o registre un nuevo pago.
                    </p>
                  </div>
                ) : (
                  sortedPayments.map(payment => {
                    const status =
                      payment.status || (payment.isVerified ? 'CONFIRMED' : 'PENDING');
                    const canEdit = payment.status === 'PENDING';
                    const canVerify =
                      (payment.status === 'PENDING' ||
                        (!payment.status && !payment.isVerified)) &&
                      payment.paymentMethod !== 'EFECTIVO' &&
                      payment.paymentMethod !== 'CHEQUE';
                    const canDelete =
                      payment.status !== 'CANCELLED' && payment.status !== 'REFUNDED';

                    return (
                      <CobrosMobilePaymentCard
                        key={payment.id}
                        payment={payment}
                        formatMoney={formatMoney}
                        methodLabel={m => METHOD_LABELS[m] || m}
                        methodClass={m =>
                          METHOD_BADGE_COLORS[m] || 'bg-slate-100 text-slate-700'
                        }
                        statusLabel={s => PAYMENT_STATUS_LABELS[s] || s}
                        statusClass={paymentStatusBadgeClass}
                        onView={() => navigate(`/payments/${payment.id}`)}
                        onEdit={
                          canEdit && hasPermission('payments.update')
                            ? () => openPaymentEdit(payment)
                            : undefined
                        }
                        onReceipt={() => handleDownloadReceipt(payment.id)}
                        onVerify={
                          canVerify && hasPermission('payments.verify')
                            ? () => handleVerifyPayment(payment.id)
                            : undefined
                        }
                        onDelete={
                          canDelete && hasPermission('payments.delete')
                            ? () => handleDeletePayment(payment.id)
                            : undefined
                        }
                        canEdit={canEdit && hasPermission('payments.update')}
                        canVerify={canVerify && hasPermission('payments.verify')}
                        canDelete={canDelete && hasPermission('payments.delete')}
                      />
                    );
                  })
                )}
              </div>

              <div className="hidden md:block">
                <DataTableCard>
                  <Table
                    data={sortedPayments}
                    columns={paymentColumns}
                    loading={paymentsLoading}
                    emptyMessage="No se encontraron pagos"
                    rowClassName="hover:bg-slate-50/80 transition-colors duration-200"
                  />
                </DataTableCard>
              </div>

              {paymentsData?.data?.totalPages != null && paymentsData.data.totalPages > 1 && (
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs text-slate-500">
                    Página {paymentsData.data.page} de {paymentsData.data.totalPages} —{' '}
                    {paymentsData.data.total} pagos en total
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentPage(p => Math.max(1, p - 1))}
                      disabled={paymentPage <= 1}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      ← Anterior
                    </button>
                    <span className="px-2 text-xs text-slate-600">
                      {paymentPage} / {paymentsData.data.totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPaymentPage(p => Math.min(paymentsData.data.totalPages, p + 1))
                      }
                      disabled={paymentPage >= paymentsData.data.totalPages}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Siguiente →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {isPanelOpen && panelMode === 'invoice' && (
          <InvoicePanel
            invoice={selectedInvoice}
            duplicateFrom={duplicateInvoice}
            clients={clients}
            lockedClientId={lockedClientId}
            onClose={closePanel}
            onSave={handleSaveInvoice}
            isSaving={createInvoiceMutation.isLoading || updateInvoiceMutation.isLoading}
          />
        )}

        {isPanelOpen && panelMode === 'payment' && (
          <PaymentPanel
            key={
              quickPayInvoice
                ? `qp-${quickPayInvoice.id}`
                : selectedPayment
                  ? `p-${selectedPayment.id}`
                  : 'new'
            }
            payment={selectedPayment}
            clients={clients}
            lockedClientId={lockedClientId}
            quickPayInvoice={quickPayInvoice}
            onClose={closePanel}
            onSave={handleSavePayment}
            isSaving={createPaymentMutation.isLoading || updatePaymentMutation.isLoading}
          />
        )}
      </div>

      <CobrosFiltersSheet
        open={filtersSheetOpen}
        onClose={() => setFiltersSheetOpen(false)}
        mode={activeTab}
        status={
          activeTab === 'pendiente'
            ? pendingFilterStatus
            : activeTab === 'facturas'
              ? invoiceFilterStatus
              : paymentFilterStatus
        }
        onStatusChange={v => {
          if (activeTab === 'pendiente') {
            setPendingFilterStatus(v);
            setPendingPage(1);
          } else if (activeTab === 'facturas') {
            setInvoiceFilterStatus(v);
            setInvoicePage(1);
          } else {
            setPaymentFilterStatus(v);
            setPaymentPage(1);
          }
        }}
        startDate={
          activeTab === 'pendiente'
            ? pendingFilterStart
            : activeTab === 'facturas'
              ? invoiceFilterStart
              : paymentFilterStart
        }
        onStartDateChange={v => {
          if (activeTab === 'pendiente') {
            setPendingFilterStart(v);
            setPendingPage(1);
          } else if (activeTab === 'facturas') {
            setInvoiceFilterStart(v);
            setInvoicePage(1);
          } else {
            setPaymentFilterStart(v);
            setPaymentPage(1);
          }
        }}
        endDate={
          activeTab === 'pendiente'
            ? pendingFilterEnd
            : activeTab === 'facturas'
              ? invoiceFilterEnd
              : paymentFilterEnd
        }
        onEndDateChange={v => {
          if (activeTab === 'pendiente') {
            setPendingFilterEnd(v);
            setPendingPage(1);
          } else if (activeTab === 'facturas') {
            setInvoiceFilterEnd(v);
            setInvoicePage(1);
          } else {
            setPaymentFilterEnd(v);
            setPaymentPage(1);
          }
        }}
        paymentMethod={paymentFilterMethod}
        onPaymentMethodChange={v => {
          setPaymentFilterMethod(v);
          setPaymentPage(1);
        }}
        paymentMethods={PAYMENT_METHODS}
        statusOptions={
          activeTab === 'pendiente'
            ? PENDING_STATUS_FILTERS
            : activeTab === 'facturas'
              ? invoiceStatusFilterOptions
              : paymentStatusFilterOptions
        }
        sortDir={listSortDir}
        onSortDirChange={setListSortDir}
        onClear={clearActiveTabFilters}
        clientLabel={selectedClient ? clientFullName(selectedClient) : 'Todos'}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        isLoading={
          deleteInvoiceMutation.isLoading ||
          markAsPaidMutation.isLoading ||
          generateMonthlyMutation.isLoading ||
          deletePaymentMutation.isLoading ||
          verifyPaymentMutation.isLoading
        }
      />

      <PaymentSuccessModal
        isOpen={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          setSuccessPayment(null);
          setSuccessCreditMinor(0);
        }}
        payment={successPayment}
        creditBalanceMinor={successCreditMinor}
      />
    </div>
  );
};

export default Cobros;
